// The fields a stop has, decided entirely by the profile.
//
// `profiles.ts` lists what each operation exposes and a field absent from
// that list does not exist here — there is no settings page where it
// comes back. School transport is the one worth looking at: no parcels
// field and no per-stop contact, because names of children never reach a
// driver's device and a stop is a place with a number of children at it.

import { profileFor, type ProfileId, type StopFieldDefinition } from "@wayfind/core";
import type { StoredStop } from "../ops/state";

export function StopFields({
  profile,
  stop,
  onChange
}: {
  profile: ProfileId;
  stop: StoredStop;
  onChange(fields: StoredStop["fields"]): void;
}): JSX.Element | null {
  const definition = profileFor(profile);
  if (!definition.stopFields.length) return null;

  const set = (field: StopFieldDefinition, value: string | number | boolean) => {
    onChange({ ...stop.fields, [field.id]: value });
  };

  const flags = definition.stopFields.filter(field => field.kind === "flag");
  const inputs = definition.stopFields.filter(field => field.kind !== "flag");

  return (
    <div className="stack stack--tight">
      {inputs.length ? (
        <div className="grid2">
          {inputs.map(field => (
            <label key={field.id} className="field">
              <span className="field__label">{field.label}</span>
              <input
                className="input"
                type={field.kind === "number" ? "number" : "text"}
                inputMode={field.kind === "number" ? "numeric" : undefined}
                min={field.kind === "number" ? 0 : undefined}
                placeholder={field.hint ?? ""}
                value={String(stop.fields[field.id] ?? "")}
                onChange={event => set(field, event.target.value)}
              />
            </label>
          ))}
        </div>
      ) : null}

      {flags.length ? (
        <div className="row">
          {flags.map(field => (
            <label key={field.id} className="checkline">
              <input
                type="checkbox"
                checked={stop.fields[field.id] === true}
                onChange={event => set(field, event.target.checked)}
              />
              <span>{field.label}</span>
            </label>
          ))}
        </div>
      ) : null}

      {profile === "pharmacy" && flags.length ? (
        <p className="field__hint">
          These two ride in front of the instruction line the driver reads at the door — the plan
          format has four fields and none of them is a tick box, so they travel as words rather than
          as a flag nobody can see.
        </p>
      ) : null}

      {profile === "school" ? (
        <p className="field__hint">
          The number expected travels as the stop’s instruction line. There is no parcels field and
          no phone number on a school run: the office holds the names, and the driver gets a place
          and a count.
        </p>
      ) : null}
    </div>
  );
}

/** One line summarising what a stop carries, for a list that is not being edited. */
export function stopSummary(profile: ProfileId, stop: StoredStop): string {
  const definition = profileFor(profile);
  const parts: string[] = [];
  for (const field of definition.stopFields) {
    const value = stop.fields[field.id];
    if (value === true) parts.push(field.label);
    else if (value !== "" && value != null && value !== false) parts.push(`${field.label} ${value}`);
  }
  return parts.join(" · ");
}
